<template>
  <div class="container">
    <BLoading :active.sync="properties.loadingStatus.isLoading" />
    <div
      v-if="!properties.loadingStatus.isLoading && (!ltrs || !ltrs.loadingStatus.isLoading)"
      class="columns"
    >
      <div class="column is-8" :class="{ 'ls-left-column': !isCustomerLead }">
        <ApplicationReview v-if="showApplicationReview" :review-link="getApplicationReviewLink()" />

        <div
          v-for="{ data, key, property } in propertiesWithGroupedServices"
          :key="key"
          class="ls-services-view--property-section"
        >
          <LsServicePromosTop
            v-if="hasServicePromos(property)"
            :property-id="property.id"
            :promo="getFirstServicePromo(property.id)"
          />

          <div class="ls-services-view--space-between-section ls--border-bottom-primary-light">
            <LsAddressCard
              :property="property"
              class="ls-services-view--space-between-section-first"
            />
            <LsButton
              v-if="!isCustomerLead"
              text="edit"
              bg-color="primary-light"
              text-color="primary"
              track-id="services_view-edit"
              @click="
                $router.push({
                  name: 'propertiesRequiredInfo',
                  params: {
                    property_id: property.property_id,
                  },
                })
              "
            />
          </div>

          <div v-if="showCustomerLeads({ property })">
            <LsProviderInfo
              :property="property"
              :show-new-hired-flow="showNewHiredFlow"
              :show-heading="!data.length"
            />
          </div>

          <h1 v-if="data.length">My Scheduled Services</h1>

          <div v-for="(schedule, idx) in data" :key="schedule.schedule_id">
            <LsScheduleServiceItem
              v-if="scheduleType({ schedule }) === 'schedule'"
              :schedule="schedule"
              :property="property"
              track-id="services_view-schedule_service_item"
              :divider-bottom="idx !== data.length"
              @click="
                $router.push({
                  name: getRouteNameForScheduleType({ type: schedule.type }),
                  params: {
                    property_id: property.property_id,
                    schedule_id: schedule.schedule_id,
                  },
                })
              "
            />
            <LsRequiredServiceItem
              v-if="scheduleType({ schedule }) === 'requiredService'"
              :required-service="schedule"
              track-id="services_view-required_service_item"
              @click="
                $router.push({
                  name: 'requiredService',
                  params: {
                    property_id: property.property_id,
                    requiredService_id: schedule.requiredService_id,
                  },
                })
              "
            />
            <LsRecommendedServiceItem
              v-if="scheduleType({ schedule }) === 'recommendation'"
              :recommendation="schedule"
              track-id="services_view-recommended_service_item"
              @click="
                $router.push({
                  name: 'recommendedService',
                  params: {
                    property_id: property.property_id,
                    id: schedule.id,
                  },
                })
              "
            />
          </div>
          <div v-for="instantQuote in property.instantQuotes" :key="instantQuote.id">
            <LsRequiredServiceItem
              v-if="
                instantQuote.type === instantQuoteType.LONG_GRASS &&
                  isLongGrassQuoteValid({ property, instantQuote })
              "
              :required-service="lgInstantQuote(instantQuote)"
              track-id="services_view-required_service_item"
              test-id="ls_required_service_item-long_grass_quote"
              @click="
                $router.push({
                  name: 'LongGrassQuoteReport',
                  params: formatLgqCardParams({ property, instantQuote }),
                })
              "
            />
            <LsPendingInstantQuoteItem
              v-if="instantQuote.type !== instantQuoteType.LONG_GRASS"
              :instant-quote="instantQuote"
              :property_id="property.property_id"
              track-id="services_view-pending_instant_quote_item"
              test-id="ls_pending_instant_quote_item"
            />
          </div>

          <LsServicePromosBottom
            v-if="hasServicePromos(property)"
            :service-promos="getServicePromos(property)"
            :property-id="property.property_id"
            test-id="ls_service_promos_bottom"
          />
          <LsSuggestedInstantQuoteItem
            v-for="suggestion in property.instant_quote_suggestions"
            v-else
            :key="suggestion.id"
            test-id="suggested-instant-quote-item"
            :suggestion="suggestion"
            track-id="services_view-suggested_instant_quote_item"
            @click="
              $router.push({
                name: 'orderServiceForm',
                params: {
                  property_id: property.property_id,
                  // TODO: extend for additional instant quote service suggestions
                  service_id: 8,
                  instant_quote_suggestion_id: suggestion.id,
                },
                query: {
                  instant_quote_suggestion_id: suggestion.id,
                },
              })
            "
          />

          <LsReactivateCard :property="property" />
          <div
            v-if="property.can_request_additional_services"
            class="ls-services-view--space-between-section ls-services-view--additional-services"
          >
            <h4 class="ls-services-view--space-between-section-first">
              Do you need a one-time cleanup, leaf removal, shrub care, or anything else?
            </h4>
            <LsButton
              text="request a quote"
              track-id="services_view-request_a_quote"
              @click="
                $router.push({
                  name: 'orderService',
                  params: {
                    property_id: property.property_id,
                  },
                })
              "
            />
          </div>
        </div>
        <LsButton
          class="ls-services-view--add-a-property"
          text="add a property"
          track-id="services_view-add_a_property"
          test-id="add_a_property"
          bg-color="primary-light"
          text-color="primary"
          @click="openAddPropertyModal"
        />
      </div>
      <div v-if="!isCustomerLead && showShareWidget" class="column ls-right-column">
        <div v-if="showShareWidget" class="ls-services-view--share-main"><LsShareMain /></div>
        <div v-else-if="isLawnStarter" class="ls-services-view--app-download">
          <AppDownloadWidget />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  LsPendingInstantQuoteItem,
  LsRecommendedServiceItem,
  LsRequiredServiceItem,
  LsScheduleServiceItem,
  LsShareMain,
  LsSuggestedInstantQuoteItem,
} from '@/components';
import ApplicationReview from '@/components/ApplicationReview/ApplicationReview.vue';
import { LsAddressCard, LsButton } from '@/components/common';
import LsProviderInfo from '@/components/LsProviderInfo/LsProviderInfo.vue';
import { themedComponentMixin } from '@/mixins';
import {
  AddPropertyModal,
  LeadsHiredDetailsModal,
  LeadsHiredFlowModal,
  ReviewLeadModal,
  ReviewModal,
} from '@/modals';
import {
  AUTH__IS_CUSTOMER_LEAD,
  FEATURE_FLAGS__GET_FLAGS,
  LTRS__GET_SCHEDULE_EVENTS,
  MY_SERVICES__PROPERTIES_WITH_GROUPED_SERVICES,
  MY_SERVICES__SHOW_SHARE_WIDGET,
  PROPERTIES__GET_FIRST_HIRED_PRO_NEEDING_DETAILS,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  PROPERTIES__GET_SERVICE_PROMOS,
  SCHEDULES__GET_SCHEDULE,
} from '@/store/types';
import _ from 'lodash';
import { lsModalService, lsStorageService } from 'ls-vue-common';
import { mapActions, mapGetters, mapState } from 'vuex';
import { AppDownloadWidget, LsReactivateCard } from './components';
import LsServicePromosBottom from './components/LsServicePromosBottom/LsServicePromosBottom.vue';
import LsServicePromosTop from './components/LsServicePromosTop/LsServicePromosTop.vue';
import { lsPropertyHelpers, lsRequiredServiceItemHelpers } from '@/helpers';
import { INSTANT_QUOTE_STATUS, INSTANT_QUOTE_TYPES } from '@/constants';

export default {
  name: 'ServicesView',
  components: {
    AppDownloadWidget,
    ApplicationReview,
    LsReactivateCard,
    LsShareMain,
    LsScheduleServiceItem,
    LsRequiredServiceItem,
    LsRecommendedServiceItem,
    LsSuggestedInstantQuoteItem,
    LsAddressCard,
    LsButton,
    LsPendingInstantQuoteItem,
    LsProviderInfo,
    LsServicePromosTop,
    LsServicePromosBottom,
  },
  mixins: [themedComponentMixin],
  computed: {
    ...mapState(['ltrs', 'properties', 'auth', 'featureFlags', 'termsOfService']),
    ...mapGetters({
      MY_SERVICES__PROPERTIES_WITH_GROUPED_SERVICES,
      MY_SERVICES__SHOW_SHARE_WIDGET,
      hiredDetailsModalProps: [PROPERTIES__GET_FIRST_HIRED_PRO_NEEDING_DETAILS],
      isCustomerLead: [AUTH__IS_CUSTOMER_LEAD],
    }),
    instantQuoteType() {
      return INSTANT_QUOTE_TYPES;
    },
    hasAgreedToTOS() {
      return this.termsOfService.hasAgreedToTOS;
    },
    propertiesWithGroupedServices() {
      return this[MY_SERVICES__PROPERTIES_WITH_GROUPED_SERVICES];
    },
    showShareWidget() {
      return this[MY_SERVICES__SHOW_SHARE_WIDGET];
    },
    showNewHiredFlow() {
      return _.get(this.featureFlags.flags, ['customerLeads.hiredDrip']);
    },
    getLeadNeedingStatus() {
      let leadNeedingStatus = null;

      _.forEach(this.propertiesWithGroupedServices, (property) => {
        const customerLead = _.find(property.property.customer_leads, (lead) => {
          return lead.check_customer_status;
        });

        if (customerLead) {
          leadNeedingStatus = {
            customerLead,
            customerLeadId: customerLead.id,
            propertyId: property.property.id,
          };
        }
      });

      return leadNeedingStatus;
    },
    getLeadNeedingReview() {
      let leadNeedingReview = null;

      _.forEach(this.propertiesWithGroupedServices, (property) => {
        const customerLead = _.find(property.property.customer_leads, (lead) => {
          return lead.awaiting_review;
        });

        if (customerLead) {
          leadNeedingReview = customerLead;
        }
      });

      return leadNeedingReview;
    },
    showApplicationReview() {
      return (
        this.featureFlags.flags['customer.app_reviews.web_application'] &&
        this.auth.user.offer_app_review &&
        !lsStorageService.get('APPLICATION_REVIEW_DISMISSED', false)
      );
    },
  },
  watch: {
    hasAgreedToTOS(newValue) {
      if (newValue) {
        this.continueModalLaunches();
      }
    },
  },
  async created() {
    await this[LTRS__GET_SCHEDULE_EVENTS]({ forceRefresh: true });
    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]({ forceRefresh: true });
    await this[PROPERTIES__GET_SERVICE_PROMOS]();

    if (this.hasAgreedToTOS) {
      this.continueModalLaunches();
    }
  },
  methods: {
    ...mapActions([
      LTRS__GET_SCHEDULE_EVENTS,
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      PROPERTIES__GET_SERVICE_PROMOS,
      SCHEDULES__GET_SCHEDULE,
      FEATURE_FLAGS__GET_FLAGS,
    ]),
    async continueModalLaunches() {
      const { is_reviewable, reviews, schedule, scheduleevent_id, contractor } = _.get(
        this.auth,
        'user.customer.last_completed_scheduleevent',
        {}
      );
      const leadNeedingStatus = this.getLeadNeedingStatus;
      const leadNeedingReview = this.getLeadNeedingReview;

      if (leadNeedingStatus) {
        this.openLeadsHiredFlowModal(leadNeedingStatus);
      } else if (this.hiredDetailsModalProps) {
        this.openLeadsHiredDetailsModal(this.hiredDetailsModalProps);
      } else if (leadNeedingReview) {
        this.openReviewLeadModal({ customerLead: leadNeedingReview });
      } else if (is_reviewable && _.isEmpty(reviews) && schedule) {
        await this[SCHEDULES__GET_SCHEDULE]({
          property_id: schedule.property_id,
          schedule_id: schedule.schedule_id,
        });

        this.openReviewModal({
          scheduleevent_id,
          schedule_id: schedule.schedule_id,
          contractorFirstName: contractor.user.first_name,
          tipMatchEligible: contractor.tier === 'platinum',
        });
      }
    },
    lgInstantQuote(quote) {
      const { id, hide_at } = quote;
      const [expirationDate] = hide_at.split(' ');

      return {
        ...quote,
        requiredService_id: id,
        expires_at: expirationDate,
        service: { name: 'Long Grass Cleanup' },
        status: lsRequiredServiceItemHelpers.getRequiredServiceItemStatusFromLGQ(quote),
      };
    },
    formatLgqCardParams({ property = {}, instantQuote = {} }) {
      const { id, next_scheduleevent_id } = lsPropertyHelpers.filterLawnMowingSchedule(property);

      return {
        property_id: property.property_id,
        report_id: instantQuote.id,
        schedule_id: id,
        scheduleevent_id: next_scheduleevent_id,
      };
    },
    isLongGrassQuoteValid({ property = {}, instantQuote = {} }) {
      const { id: instantQuoteId, instant_quote_status } = instantQuote;
      if (instant_quote_status !== INSTANT_QUOTE_STATUS.CUSTOMER_PENDING) return false;

      const {
        id: scheduleId,
        next_scheduleevent_id: scheduleEventId,
      } = lsPropertyHelpers.filterLawnMowingSchedule(property);

      const isRejected =
        lsStorageService.get(
          [property.property_id, scheduleId, scheduleEventId, instantQuoteId].join('')
        ) || false;

      return !isRejected;
    },
    scheduleType({ schedule }) {
      return _.head(_.split(schedule.key, '_'));
    },
    hasServicePromos(property) {
      return !this.isCustomerLead && property.is_active && this.getServicePromos(property).length;
    },
    getFirstServicePromo(propertyId) {
      const [firstPromo] = this.properties.servicePromosByPropertyId[propertyId];
      return firstPromo;
    },
    getServicePromos(property) {
      return _.drop(_.get(this.properties.servicePromosByPropertyId, `[${property.id}]`), 1);
    },
    getRouteNameForScheduleType({ type }) {
      if (type === 'manualquote') {
        return 'quoteDetails';
      }
      if (type === 'instant_quote') {
        return 'instantQuoteDetails';
      }
      return 'serviceDetails';
    },
    openAddPropertyModal() {
      lsModalService.open({
        parent: this,
        component: AddPropertyModal,
      });
    },
    openLeadsHiredFlowModal(props) {
      lsModalService.open({
        parent: this,
        component: LeadsHiredFlowModal,
        width: 760,
        props,
      });
    },
    openReviewModal({ scheduleevent_id, schedule_id, contractorFirstName, tipMatchEligible }) {
      lsModalService.open({
        parent: this,
        component: ReviewModal,
        width: 760,
        props: {
          scheduleevent_id,
          schedule_id,
          contractorFirstName,
          tipMatchEligible,
        },
      });
    },
    openLeadsHiredDetailsModal(props) {
      lsModalService.open({
        parent: this,
        component: LeadsHiredDetailsModal,
        props,
      });
    },
    openReviewLeadModal({ customerLead }) {
      const hiredLeadContact = _.find(customerLead.customer_lead_contacts, (contact) => {
        return contact.status === 'hired';
      });

      if (hiredLeadContact) {
        lsModalService.open({
          parent: this,
          component: ReviewLeadModal,
          width: 760,
          props: {
            customerLead,
            propertyId: customerLead.property_id,
            firstName: hiredLeadContact.contractor.user.first_name,
          },
        });
      }
    },
    getCustomerLeads({ property }) {
      return _.get(property, 'customer_leads[0]');
    },
    showCustomerLeads({ property }) {
      const lead = this.getCustomerLeads({ property });
      return lead && lead.status !== 'closed';
    },
    getApplicationReviewLink() {
      const { propertiesById, applicationReviewPropertyId } = this.properties;

      const propertyToReviewFlow =
        applicationReviewPropertyId || Object.keys(propertiesById).find(Boolean);

      const propertyToReview = propertiesById[propertyToReviewFlow];

      const { url_web: reviewLink } =
        propertyToReview && propertyToReview.priced.market.review_target;

      return reviewLink;
    },
  },
};
</script>

<style lang="scss" scoped>
.columns {
  justify-content: center;
}

.ls-services-view--property-section {
  border-bottom: 1px solid $primary;
  margin-bottom: 20px;
  &:not(:last-of-type) {
    margin-bottom: 40px;
  }
}

.ls-services-view--margin {
  margin: 20px 0;
}

.ls-services-view--space-between-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  flex-wrap: wrap;
  margin-bottom: 20px;

  .ls-services-view--space-between-section-first {
    margin-bottom: 20px;
    @media (min-width: $desktop) {
      margin-bottom: 0;
      max-width: 374px;
    }
  }
}

.ls-services-view--additional-services {
  margin-bottom: 0;
}

.ls-services-view--add-a-property {
  display: flex;
  margin-left: auto;
}

.ls-services-view--share-main {
  height: 360px;
}

@media (min-width: $desktop) {
  .ls-services-view--share-main {
    height: 460px;
  }

  .ls-services-view--app-download {
    height: 645px;
  }
}
.themed-app {
  .ls-services-view--property-section {
    border-color: $theme-color-primary;
  }
}
</style>
